import { relations, sql } from 'drizzle-orm';
import { index, boolean, date, numeric, pgEnum, pgTable, text, timestamp, uuid, check, integer } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { Status } from '../../util/schemas.js';
import { companies, stockLocations, units } from './basic-data.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
import { tradeNames, consumables } from './products.js';
import { user } from './auth.js';
import { patientTherapySequence } from './patient-therapy.js';
import { orders } from './production-orders.js';
// #region Batches
// (supplier is batch-level)
export const batchStatus = pgEnum('batch_status', [Status.enum.active, Status.enum.inactive]);
export const batches = pgTable('batches', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    tradeNameId: uuid('trade_name_id').references(() => tradeNames.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    consumableId: uuid('consumable_id').references(() => consumables.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    supplierCompanyId: uuid('supplier_company_id').references(() => companies.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    lotNumber: text('lot_number').notNull(),
    // the day printed on the vial, with no time: the batch is usable through that day wherever it is read (CY-371)
    expiryDate: date('expiry_date').notNull(),
    receivedAt: timestamp('received_at', { withTimezone: true }),
    receivedQuantity: numeric('received_quantity'), // initial quantity of the batch
    quantity: numeric('quantity'), // quantity left on the batch
    stockLocationId: uuid('stock_location_id').references(() => stockLocations.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    note: text('note'),
    // Raw ZIV segment text, retained for outbound replay compatibility.
    rawZiv: text('raw_ziv'),
    // AMMS product ID and description from the ORR's RQD-3 (components 1 and 2), reported back
    // in RQD-3 of the rest vial ZRM^O01 message.
    ammsProductId: text('amms_product_id'),
    ammsProductDescription: text('amms_product_description'),
    /**
     * Refers to the expired batches (acknowledget expiry at).
     */
    acknowledgedAt: timestamp('acknowledged_at', { withTimezone: true }),
    status: batchStatus('status').default(Status.enum.active),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_batches_fefo').on(t.tradeNameId, t.expiryDate),
    index('idx_batches_lot_number').on(t.lotNumber),
    index('idx_batches_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // stock list filters by supplier/location and consumable batches
    index('idx_batches_stock_location').on(t.stockLocationId),
    index('idx_batches_supplier_company').on(t.supplierCompanyId),
    index('idx_batches_consumable').on(t.consumableId),
    // batch-number search uses ILIKE on the lot
    index('idx_batches_lot_number_trgm').using('gin', sql `${t.lotNumber} gin_trgm_ops`),
    check('batches_exactly_one_entity', sql `(trade_name_id IS NOT NULL AND consumable_id IS NULL) OR (trade_name_id IS NULL AND consumable_id IS NOT NULL)`),
]);
// #endregion
// #region Stocks
// active: quantity > min stock
// low: 0 < quantity <= min stock
// depleted: -> quantity = 0
export const StockStatus = z.enum(['active', 'low', 'depleted']);
export const stockStatus = pgEnum('stock_status', [
    StockStatus.enum.active,
    StockStatus.enum.low,
    StockStatus.enum.depleted,
]);
export const stocks = pgTable('stocks', {
    id: uuidv7('id').primaryKey(),
    name: text('name').notNull(),
    tradeNameId: uuid('trade_name_id').references(() => tradeNames.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    consumableId: uuid('consumable_id').references(() => consumables.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    totalQuantity: numeric('total_quantity'),
    earliestExpiryDate: timestamp('expiry_date'),
    hasUnacknowledgedExpiredBatches: boolean('has_unacknowledged_expired_batches').default(false),
    stockStatus: stockStatus('stock_status'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_stocks_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    index('idx_stocks_trade_name').on(t.tradeNameId),
    index('idx_stocks_consumable').on(t.consumableId),
    // the stock list sorts/pages by name and expiry and filters by status
    index('idx_stocks_name').on(t.name),
    index('idx_stocks_earliest_expiry').on(t.earliestExpiryDate),
    index('idx_stocks_status').on(t.stockStatus),
    check('stocks_exactly_one_entity', sql `(trade_name_id IS NOT NULL AND consumable_id IS NULL) OR (trade_name_id IS NULL AND consumable_id IS NOT NULL)`),
]);
// #endregion
// #region Rest vials, returns, discards
export const REST_VIAL_STATUS = ['AVAILABLE', 'IN_USE', 'USED', 'DISCARDED'];
export const RestVialStatus = z.enum(REST_VIAL_STATUS);
export const restVialStatusEnum = pgEnum('rest_vial_status', [
    RestVialStatus.enum.AVAILABLE,
    RestVialStatus.enum.IN_USE,
    RestVialStatus.enum.USED,
    RestVialStatus.enum.DISCARDED,
]);
export const restVials = pgTable('rest_vials', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    internalId: integer('internal_id'), // short identifier, reused daily
    // Stable, permanently-incrementing identifier (unlike `internalId`, which is recycled daily).
    // Used as the Rest Vial Identifier in outbound AMMS messages (e.g. ZRM ORC-3/RQD-1).
    restVialNumber: integer('rest_vial_number').generatedAlwaysAsIdentity(),
    sequenceId: uuidv7('sequence_id'), // prescription id
    tradeNameId: uuidv7('trade_name_id'),
    amount: numeric('amount'),
    stockLocationId: uuidv7('stock_location_id'),
    expiryDate: timestamp('expiry_date', { withTimezone: true, mode: 'string' }),
    batchId: uuidv7('batch_id'),
    status: restVialStatusEnum('status'),
    ...userActions(),
    ...timestamps(),
}, t => [
    index('idx_rest_vials_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    index('idx_rest_vials_status').on(t.status),
    index('idx_rest_vials_batch').on(t.batchId),
    index('idx_rest_vials_trade_name').on(t.tradeNameId),
    index('idx_rest_vials_stock_location').on(t.stockLocationId),
    index('idx_rest_vials_sequence').on(t.sequenceId),
]);
export const RETURN_REASONS = [
    'PATIENT_NOT_PRESENT',
    'TREATMENT_POSTPONED',
    'DOSE_CANCELLED',
    'PRODUCT_DAMAGED',
    'OTHER',
];
export const ReturnReason = z.enum(RETURN_REASONS);
export const returnReasonEnum = pgEnum('return_reason', [
    ReturnReason.enum.PATIENT_NOT_PRESENT,
    ReturnReason.enum.TREATMENT_POSTPONED,
    ReturnReason.enum.DOSE_CANCELLED,
    ReturnReason.enum.PRODUCT_DAMAGED,
    ReturnReason.enum.OTHER,
]);
export const RETURN_STATUS = ['AVAILABLE', 'IN_USE', 'USED', 'DISCARDED'];
export const ReturnStatus = z.enum(RETURN_STATUS);
export const returnStatusEnum = pgEnum('return_status', [
    ReturnStatus.enum.AVAILABLE,
    ReturnStatus.enum.IN_USE,
    ReturnStatus.enum.USED,
    ReturnStatus.enum.DISCARDED,
]);
export const returns = pgTable('returns', {
    id: uuidv7('id').primaryKey(),
    medicationName: text('medication_name'), // name of the substance(s). eg. "Cisplatin" or "Cisplatin + Paclitaxel"
    medicationForm: text('medication_form'), // the form in which is prepared. eg. "50 mg in Glucose 5%"
    carrierId: uuidv7('carrier_id'),
    sequenceId: uuidv7('sequence_id'), // prescription id
    stockLocationId: uuidv7('stock_location_id'),
    inUse: boolean('in_use').default(false), // to be deleted - use status instead
    expiryDate: timestamp('expiry_date', { withTimezone: true, mode: 'string' }),
    reason: returnReasonEnum('reason'), // reason for returning the product
    note: text('note'), // note for when 'other' reason is selected
    status: returnStatusEnum('status'),
    ...userActions(),
    ...timestamps(),
}, t => [
    index('idx_returns_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    index('idx_returns_status').on(t.status),
    // returns of a prescription / a carrier, and the returns list filters
    index('idx_returns_sequence').on(t.sequenceId),
    index('idx_returns_carrier').on(t.carrierId),
    index('idx_returns_stock_location').on(t.stockLocationId),
]);
// discard reasons are slightly different on rest vials vs returns
export const DISCARD_REST_VIAL_REASONS = [
    'AUTO_EXPIRED',
    'BROKEN_SPILLED',
    'CONTAMINATION_SUSPECTED',
    'EOD_CLEANOUT',
    'PATIENT_SPECIFIC',
    'ABORTED',
    'SPOILED',
    'OTHER',
];
export const DISCARD_RETURN_REASONS = [
    'AUTO_EXPIRED',
    'STABILITY_CONCERN',
    'VOLUME_INSUFFICIENT',
    'CONTAMINATION_SUSPECTED',
    'EOD_CLEANOUT',
    'PATIENT_SPECIFIC',
    'SPOILED',
    'OTHER',
];
// a subset of the rest vial reasons, so the discard_reason enum stays unchanged
export const DISCARD_BATCH_REASONS = ['BROKEN_SPILLED', 'CONTAMINATION_SUSPECTED', 'OTHER'];
export const DiscardRestVialReason = z.enum(DISCARD_REST_VIAL_REASONS);
export const DiscardReturnReason = z.enum(DISCARD_RETURN_REASONS);
export const DiscardBatchReason = z.enum(DISCARD_BATCH_REASONS);
export const DiscardReason = z.union([DiscardRestVialReason, DiscardReturnReason, DiscardBatchReason]);
export const ALL_REASONS = Array.from(new Set([...DISCARD_REST_VIAL_REASONS, ...DISCARD_RETURN_REASONS]));
export const discardReasonEnum = pgEnum('discard_reason', ALL_REASONS);
export const discards = pgTable('discards', {
    id: uuidv7('id').primaryKey(),
    batchId: uuid('batch_id'),
    restVialId: uuid('rest_vial_id'),
    returnId: uuid('return_id'),
    lastLocationId: uuidv7('last_location_id'),
    reason: discardReasonEnum('reason'),
    note: text('note'),
    ...userActions(),
    ...timestamps(),
}, t => [
    index('idx_discards_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    index('idx_discards_batch').on(t.batchId),
    index('idx_discards_rest_vial').on(t.restVialId),
    index('idx_discards_return').on(t.returnId),
    check('discards_exactly_one_entity', sql `(batch_id IS NOT NULL AND rest_vial_id IS NULL AND return_id IS NULL)
        OR (batch_id IS NULL AND rest_vial_id IS NOT NULL AND return_id IS NULL)
        OR (batch_id IS NULL AND rest_vial_id IS NULL AND return_id IS NOT NULL)`),
]);
// #endregion
// #region Stock movements
// One row per change of a batch quantity (received, consumed, rest vial created/taken, discarded,
// returned, corrected, transferred). Movements are append-only: they are never edited or deleted.
export const StockMovementType = z.enum([
    'received',
    'consumed',
    'rest_vial_created',
    'rest_vial_taken',
    'discarded',
    'returned',
    'corrected',
    'transferred',
]);
export const stockMovementTypeEnum = pgEnum('stock_movement_type', [
    StockMovementType.enum.received,
    StockMovementType.enum.consumed,
    StockMovementType.enum.rest_vial_created,
    StockMovementType.enum.rest_vial_taken,
    StockMovementType.enum.discarded,
    StockMovementType.enum.returned,
    StockMovementType.enum.corrected,
    StockMovementType.enum.transferred,
]);
export const StockMovementSource = z.enum(['new_pack', 'rest_vial', 'both']);
export const stockMovementSourceEnum = pgEnum('stock_movement_source', [
    StockMovementSource.enum.new_pack,
    StockMovementSource.enum.rest_vial,
    StockMovementSource.enum.both,
]);
export const stockMovements = pgTable('stock_movements', {
    id: uuidv7('id').primaryKey(),
    type: stockMovementTypeEnum('type').notNull(),
    performedAt: timestamp('performed_at', { withTimezone: true }).defaultNow().notNull(),
    batchId: uuid('batch_id').references(() => batches.id, { onDelete: 'set null' }),
    // signed: positive into stock, negative out of stock
    quantity: numeric('quantity'),
    quantityUnitId: uuid('quantity_unit_id').references(() => units.id, { onDelete: 'set null' }),
    packages: numeric('packages'),
    source: stockMovementSourceEnum('source'),
    reason: discardReasonEnum('reason'),
    note: text('note'),
    stockLocationId: uuid('stock_location_id').references(() => stockLocations.id, { onDelete: 'set null' }),
    // links the movement can carry
    sequenceId: uuid('sequence_id'), // prescription
    orderId: uuid('order_id'), // production / picking list
    restVialId: uuid('rest_vial_id'),
    returnId: uuid('return_id'),
    discardId: uuid('discard_id'),
    deliveryNoteNumber: text('delivery_note_number'),
    ...userActions(),
    ...timestamps(),
}, t => [
    index('idx_stock_movements_performed_at').on(t.performedAt),
    index('idx_stock_movements_batch').on(t.batchId),
    // the movements report filters by type/source/location/user and keeps the time order
    index('idx_stock_movements_type_performed').on(t.type, t.performedAt),
    index('idx_stock_movements_source_performed').on(t.source, t.performedAt),
    index('idx_stock_movements_location_performed').on(t.stockLocationId, t.performedAt),
    index('idx_stock_movements_created_by').on(t.createdBy, t.performedAt),
    index('idx_stock_movements_sequence').on(t.sequenceId),
]);
// #endregion
// #region Relations
export const batchesRelations = relations(batches, ({ one }) => ({
    created: one(user, { fields: [batches.createdBy], references: [user.id] }),
    tradeName: one(tradeNames, {
        fields: [batches.tradeNameId],
        references: [tradeNames.id],
    }),
    consumable: one(consumables, {
        fields: [batches.consumableId],
        references: [consumables.id],
    }),
    supplierCompany: one(companies, {
        fields: [batches.supplierCompanyId],
        references: [companies.id],
    }),
    stockLocation: one(stockLocations, {
        fields: [batches.stockLocationId],
        references: [stockLocations.id],
    }),
}));
export const stocksRelations = relations(stocks, ({ one }) => ({
    created: one(user, { fields: [stocks.createdBy], references: [user.id] }),
    tradeName: one(tradeNames, {
        fields: [stocks.tradeNameId],
        references: [tradeNames.id],
    }),
    consumable: one(consumables, {
        fields: [stocks.consumableId],
        references: [consumables.id],
    }),
}));
export const restVialsRelations = relations(restVials, ({ one }) => ({
    created: one(user, { fields: [restVials.createdBy], references: [user.id] }),
    tradeName: one(tradeNames, {
        fields: [restVials.tradeNameId],
        references: [tradeNames.id],
    }),
    patientTherapySequence: one(patientTherapySequence, {
        fields: [restVials.sequenceId],
        references: [patientTherapySequence.id],
    }),
    stockLocation: one(stockLocations, {
        fields: [restVials.stockLocationId],
        references: [stockLocations.id],
    }),
    batch: one(batches, {
        fields: [restVials.batchId],
        references: [batches.id],
    }),
}));
export const returnsRelations = relations(returns, ({ one }) => ({
    created: one(user, { fields: [returns.createdBy], references: [user.id] }),
    patientTherapySequence: one(patientTherapySequence, {
        fields: [returns.sequenceId],
        references: [patientTherapySequence.id],
    }),
    stockLocation: one(stockLocations, {
        fields: [returns.stockLocationId],
        references: [stockLocations.id],
    }),
}));
export const discardsRelations = relations(discards, ({ one }) => ({
    created: one(user, { fields: [discards.createdBy], references: [user.id] }),
    batch: one(batches, {
        fields: [discards.batchId],
        references: [batches.id],
    }),
    restVial: one(restVials, {
        fields: [discards.restVialId],
        references: [restVials.id],
    }),
    return: one(returns, {
        fields: [discards.returnId],
        references: [returns.id],
    }),
    lastLocation: one(stockLocations, {
        fields: [discards.lastLocationId],
        references: [stockLocations.id],
    }),
}));
export const stockMovementsRelations = relations(stockMovements, ({ one }) => ({
    created: one(user, { fields: [stockMovements.createdBy], references: [user.id] }),
    batch: one(batches, { fields: [stockMovements.batchId], references: [batches.id] }),
    quantityUnit: one(units, { fields: [stockMovements.quantityUnitId], references: [units.id] }),
    stockLocation: one(stockLocations, { fields: [stockMovements.stockLocationId], references: [stockLocations.id] }),
    patientTherapySequence: one(patientTherapySequence, {
        fields: [stockMovements.sequenceId],
        references: [patientTherapySequence.id],
    }),
    order: one(orders, { fields: [stockMovements.orderId], references: [orders.id] }),
    restVial: one(restVials, { fields: [stockMovements.restVialId], references: [restVials.id] }),
    return: one(returns, { fields: [stockMovements.returnId], references: [returns.id] }),
    discard: one(discards, { fields: [stockMovements.discardId], references: [discards.id] }),
}));
// #endregion
//# sourceMappingURL=stocks.js.map